import { motion } from "framer-motion";
import {
  PhoneCall,
  MousePointerClick,
  Repeat,
  Clock,
  ShieldCheck,
  Calendar,
  MessageSquare,
  ArrowUpRight,
} from "lucide-react";

const SERVICES_IMAGE =
  "https://images.pexels.com/photos/6809658/pexels-photo-6809658.jpeg";

const SERVICES = [
  {
    id: "engagement",
    icon: PhoneCall,
    number: "01",
    title: "AI voice receptionist",
    tagline: "Live patient engagement",
    description:
      "Private patients expect instant, professional responses. Speed-to-lead is the single biggest conversion factor in elective healthcare. Our AI voice receptionist replies in seconds over a call — so the next clinic on Google never gets the call.",
    points: [
      "Sub-60-second call answering",
      "AI agent trained on your treatments & tone",
      "Seamless handover to your team when needed",
    ],
  },
  {
    id: "widget",
    icon: MousePointerClick,
    number: "02",
    title: "Website chat bot",
    tagline: "Capture the traffic you're already paying for",
    description:
      "Our discreet, on-brand chatbot engages every visitor, qualifies the enquiry, and books consultations directly into your calendar — so the SEO and paid advertisement spend you're already making stops walking off the page unanswered.",
    points: [
      "Optimised on-site engagement",
      "Treatment-specific lead qualification",
      "Direct calendar booking integration",
    ],
  },
  {
    id: "reactivation",
    icon: Repeat,
    number: "03",
    title: "Patient reactivation campaigns (SMS)",
    tagline: "Seek revenue even when your clinic is not operational",
    description:
      "Most clinics have enquiry lists worth tens of thousands in latent revenue. A targeted SMS reactivation campaign is low-cost, high-return and a quick win — typically paying for the entire engagement within weeks.",
    points: [
      "Targeted SMS campaigns",
      "Tracked patient demands",
      "Compliance-aware opt-out handling",
    ],
  },
];

const META = [
  { icon: Clock, label: "Average reply: under 60 seconds" },
  { icon: ShieldCheck, label: "GDPR & patient-data aware" },
  { icon: Calendar, label: "Booking integrations" },
  { icon: MessageSquare, label: "Voice-first AI receptionist" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.9,
      ease: [0.22, 1, 0.36, 1],
      delay: i * 0.08,
    },
  }),
};

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-28 sm:py-40"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
        {/* Editorial section header */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-120px" }}
          variants={fadeUp}
          className="max-w-3xl"
        >
          <p className="text-xs tracking-[0.35em] uppercase text-slate-500 font-bold mb-8 flex items-center gap-3">
            <span className="inline-block w-8 h-px bg-slate-600" />
            Services
          </p>
          <h2 className="font-heading text-3xl sm:text-5xl lg:text-6xl tracking-tighter font-medium text-white leading-[1.03]">
            Three systems. One outcome:{" "}
            <span className="text-slate-500">no patient left behind.</span>
          </h2>
          <p className="mt-8 text-base sm:text-lg leading-relaxed text-slate-400 max-w-2xl">
            ProCliniq aims to engage every enquiry — live, on-site and from
            your dormant database — to optimise your clinic revenue.
          </p>
        </motion.div>

        {/* Service cards */}
        <div className="mt-20 grid grid-cols-1 lg:grid-cols-3 gap-5">
          {SERVICES.map((s, idx) => (
            <motion.article
              key={s.id}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeUp}
              custom={idx}
              data-testid={`service-card-${s.id}`}
              className="group relative bg-slate-900/60 backdrop-blur-sm border border-white/10 rounded-sm p-8 sm:p-10 transition-all duration-500 hover:-translate-y-1 hover:border-white/25 hover:bg-slate-900/80 flex flex-col overflow-hidden"
            >
              {/* subtle chapter number background */}
              <span
                aria-hidden
                className="absolute -top-6 -right-2 font-heading text-[9rem] leading-none font-semibold text-white/[0.03] select-none pointer-events-none"
              >
                {`0${idx + 1}`}
              </span>

              <div className="flex items-center justify-between mb-10 relative">
                <span className="grid place-items-center w-10 h-10 rounded-sm border border-white/15 text-white">
                  <s.icon className="w-4 h-4" />
                </span>
                <span className="text-[10px] uppercase tracking-[0.28em] text-slate-500 font-bold">
                  {s.number}
                </span>
              </div>

              <h3 className="font-heading text-2xl sm:text-3xl text-white tracking-tight font-medium relative">
                {s.title}
              </h3>
              <p className="mt-3 text-xs uppercase tracking-[0.2em] text-white/70 font-bold relative">
                {s.tagline}
              </p>
              <p className="mt-6 text-slate-400 leading-relaxed text-sm relative">
                {s.description}
              </p>

              <ul className="mt-auto pt-10 space-y-3 border-t border-white/10 mt-10 relative">
                {s.points.map((p) => (
                  <li
                    key={p}
                    className="flex items-start gap-3 text-sm text-slate-300"
                  >
                    <ArrowUpRight className="w-3.5 h-3.5 mt-1 text-white/60 shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </motion.article>
          ))}
        </div>

        {/* Editorial imagery + meta strip */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeUp}
          className="mt-20 grid grid-cols-1 lg:grid-cols-5 gap-5"
        >
          {/* Spotlighted, clipped product photography */}
          <div
            data-testid="services-image"
            className="lg:col-span-3 relative rounded-sm overflow-hidden border border-white/10 min-h-[320px]"
          >
            <div className="absolute inset-0 bg-slate-950" />
            <img
              src={SERVICES_IMAGE}
              alt="Receptionist consulting a patient at a modern medical clinic"
              className="absolute inset-0 w-full h-full object-cover grayscale opacity-40 scale-105 transition-transform duration-[2s] ease-out hover:scale-110"
              loading="lazy"
            />
            {/* Directional spotlight */}
            <div
              aria-hidden
              className="absolute inset-0"
              style={{
                background:
                  "radial-gradient(circle at 30% 40%, rgba(124,147,255,0.18) 0%, rgba(2,6,23,0.9) 55%, #020617 100%)",
              }}
            />
            {/* Corner ticks (editorial clipped frames) */}
            <div aria-hidden className="absolute top-4 left-4 w-6 h-6 border-t border-l border-white/40" />
            <div aria-hidden className="absolute top-4 right-4 w-6 h-6 border-t border-r border-white/40" />
            <div aria-hidden className="absolute bottom-4 left-4 w-6 h-6 border-b border-l border-white/40" />
            <div aria-hidden className="absolute bottom-4 right-4 w-6 h-6 border-b border-r border-white/40" />

            <div className="relative p-10 sm:p-12 h-full flex flex-col justify-end min-h-[320px]">
              <p className="text-[10px] tracking-[0.35em] uppercase text-slate-400 font-bold">
                Built for the front desk you wish you had
              </p>
              <p className="mt-4 text-white text-2xl sm:text-3xl font-heading tracking-tight max-w-xl leading-[1.15]">
                Replies during ongoing clinics, weekends, and the 5pm rush —
                without hiring another receptionist.
              </p>
            </div>
          </div>

          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10 rounded-sm overflow-hidden">
            {META.map((m, i) => (
              <div
                key={m.label}
                data-testid={`services-meta-${i}`}
                className="bg-slate-950 p-6 flex items-start gap-3 transition-colors duration-500 hover:bg-slate-900"
              >
                <m.icon className="w-4 h-4 text-slate-300 mt-0.5" />
                <span className="text-sm text-slate-300 leading-snug">
                  {m.label}
                </span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
